/**
 * @file StatusBarAlerts.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-15
 * @modified 2026-11-03
 * @description The status bar's "Alerts" for standard users and admins (Cam,
 *              15 Oct 2026 — "can users get a notification in the task bar —
 *              nice and bold and using color-error — like admins do for admin
 *              log issues?").
 *
 * Shown only while the signed-in person has at least one unacknowledged alert
 * (an item of an error or warning notice — useNoticeAlerts, the 20-second
 * summary poll). Styled as the admins' "Issues Logged" (the shared
 * .status-alert class in Dashboard.css); admins see both. Never for reps —
 * no notice is for them.
 *
 * Clicking it opens the alerts window: the same list as the Notices widget
 * (NoticeList via useNotices), where each row opens its record and can be
 * acknowledged — for you alone — or the whole notice at once. The badge
 * counts down as soon as you acknowledge; an issue fixed by anyone leaves it
 * within one poll.
 *
 * 1.1.0 (2026-11-03): a persistent alert (invoices approved here, not yet in
 * Xero) cannot be acknowledged and always counts; its rows' own actions
 * ("Send to Xero now") work here.
 */

import { useState } from 'react';
import Modal from './Modal';
import NoticeList from './NoticeList';
import useNotices from '../hooks/useNotices';
import usePersistedState from '../hooks/usePersistedState';
import { useNoticeAlerts } from '../hooks/useNoticeAlerts';
import { badgeOf } from '../utils/permissions';

const BADGES = new Set(['ADMIN', 'USER']);

/** Does this person get the Alerts at all? Pure. */
export function alertsFor(user) {
  return !!user && BADGES.has(badgeOf(user));
}

/** The list, mounted only while the window is open. */
function AlertsBody() {
  const { data, loading, failed, load, acknowledge, undo, busy, error, runAction, actionBusy, info } = useNotices();
  const [collapsed, setCollapsed] = usePersistedState('noticeAlerts.collapsed', {});
  const toggle = (key) => setCollapsed(prev => ({ ...(prev || {}), [key]: !(prev || {})[key] }));
  return (
    <div className="notice-alerts-list">
      <p className="notice-alerts-intro">
        Acknowledging is just for you: it stops counting here, everyone else still sees it, and it goes for
        everybody once it is fixed. A pinned alert cannot be acknowledged — it stays until it is put right.
      </p>
      <NoticeList
        data={data}
        loading={loading}
        failed={failed}
        onRetry={load}
        collapsed={collapsed || {}}
        onToggle={toggle}
        onAcknowledge={acknowledge}
        onUndo={undo}
        busy={busy}
        error={error}
        onAction={runAction}
        actionBusy={actionBusy}
        info={info}
      />
    </div>
  );
}

export function NoticeAlertsModal({ isOpen, onClose }) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Alerts" size="lg" className="notice-alerts">
      {isOpen && <AlertsBody />}
    </Modal>
  );
}

export default function StatusBarAlerts({ user }) {
  const { count, bySeverity } = useNoticeAlerts();
  const [open, setOpen] = useState(false);
  if (!alertsFor(user)) return null;
  const errors = bySeverity?.error || 0;
  const title = `${count} alert${count === 1 ? '' : 's'} not yet acknowledged by you`
    + `${errors ? ` (${errors} to put right in Xero or the system)` : ''} — open the list`;
  return (
    <>
      {count > 0 && (
        <div
          className="status-item status-alert notice-alerts-status clickable"
          onClick={() => setOpen(true)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setOpen(true);
            }
          }}
          title={title}
        >
          <span className="material-symbols-rounded">notifications_active</span>
          Alerts
          <span className="status-alert-count">{count}</span>
        </div>
      )}
      <NoticeAlertsModal isOpen={open} onClose={() => setOpen(false)} />
    </>
  );
}
